import _ from 'underscore';

import {
  DataTable,
  DataTableConfig,
  ImportedRow,
  ImportedRowUpdate,
  Row,
} from 'notebook/data-tables/DataTableConstants';
import {addRowToDataTable, generateDataTableRow} from 'notebook/data-tables/DataTableUtil';
import toColumnLabel from 'notebook/structured-tables/columns/toColumnLabel';

function _updateRow(
  dataTableConfig: DataTableConfig,
  row: Row,
  cellUpdates: Array<{columnName: string; text: string}>
): Row {
  return {
    ...row,
    cells: row.cells.map((cell, j) => {
      const columnName = toColumnLabel({
        structuredTableColumn: dataTableConfig.columns[j],
        schemas: [dataTableConfig.assaySchema],
      });
      const cellUpdate = _.findWhere(cellUpdates, {columnName});
      if (!cellUpdate) {
        return cell;
      }
      return {...cell, text: cellUpdate.text, typedValue: null};
    }),
  };
}

/**
 * Immutably add rows to `dataTable` from `importedRows`, using column defaults if cell values are not
 * specified.
 *
 * @typedef {Object} ImportedRow
 * @property {Object} dataKey - ids of the row's linked item
 * @property {string} dataKey.stageRunId - id of stage run, if linking to a stage run sample
 * @property {string} dataKey.stageRunSampleId - id of sample, if linking to a stage run sample
 * @property {Object[]} cellValues - an array of values for cells in a given row
 * @property {string} cellValues[].columnName - the dataTable column name to match on
 * @property {string} cellValues[].text - the text to set on the matching cell
 *
 * @param {Object} dataTable - data table to immutably update
 * @param {ImportedRow[]} importedRows - rows to add into `dataTable`
 * @returns {Object} - updated data table
 */
export function insertRowsFromSpreadsheet(dataTable: DataTable, importedRows: Array<ImportedRow>): DataTable {
  let newDataTable = dataTable;
  for (const {dataKey, cellValues} of importedRows) {
    const newRowIndex = newDataTable.rows.length;
    let newRow = generateDataTableRow(dataTable.config, newRowIndex, dataKey);
    newRow = _updateRow(dataTable.config, newRow, cellValues);
    newDataTable = addRowToDataTable(newDataTable, newRow);
  }
  return newDataTable;
}

function _updateRowFromSpreadsheet(
  dataTable: DataTable,
  importedRows: Array<ImportedRowUpdate>,
  row: Row
): Row {
  const importedRow = _.findWhere(importedRows, {euid: row.euid});
  if (!importedRow) {
    return row;
  }
  return _updateRow(dataTable.config, row, importedRow.cellUpdates);
}

/**
 * Immutably update cell values in any rows in `dataTable` that are referenced in `importedRows`.
 *
 * @typedef {Object} ImportedRowUpdate
 * @property {string} euid - the euid of the dataTable row to update
 * @property {Object[]} cellUpdates - an array of updates for cells in a given row
 * @property {string} cellUpdates[].columnName - the dataTable column name to match on
 * @property {string} cellUpdates[].text - the text to set on the matching cell
 *
 * @param {Object} dataTable - data table to immutably update
 * @param {ImportedRow[]} importedRows - updates to merge into `dataTable`
 * @return {Object} - updated data table
 */
export function updateRowsFromSpreadsheet(
  dataTable: DataTable,
  importedRows: Array<ImportedRowUpdate>
): DataTable {
  return {
    ...dataTable,
    rows: dataTable.rows.map((row) => _updateRowFromSpreadsheet(dataTable, importedRows, row)),
  };
}
